/**
 * Comprehensive validation utility for citation forms
 * Validates required fields, dates, authors, URLs, and other field types
 */

/**
 * Validates if a date string matches APA acceptable date patterns
 * Accepts: "2024", "2024, January", "2024, January 15", "n.d."
 * @param {string} dateValue - The date value to validate
 * @param {string} category - The citation category (optional) for category-specific error messages
 */
export function validateDate(dateValue, category = '') {
	// Get category-specific error messages
	const getDateErrorMessage = (messageType) => {
		const categoryMessages = {
			'Book & Chapter': {
				required: 'Enter the valid date',
				whitespace: 'Date cannot contain only whitespace.',
				invalidFormat:
					'Invalid date format. Use "2024", "2024, January", "2024, January 15", "January 15, 2024", or "n.d."'
			},
			Periodicals: {
				required: 'Enter the publication date.',
				whitespace: 'Publication date cannot contain only whitespace.',
				invalidFormat:
					'Invalid date format. Use "2024", "2024, January", "2024, January 15", "January 15, 2024", or "n.d."'
			},
			'Web Content': {
				required: 'Enter the publication date.',
				whitespace: 'Publication date cannot contain only whitespace.',
				invalidFormat:
					'Invalid date format. Use "2024", "2024, January", "2024, January 15", "January 15, 2024", or "n.d."'
			},
			'Government & Institutional Publications': {
				required: 'Enter the release date.',
				whitespace: 'Release date cannot contain only whitespace.',
				invalidFormat:
					'Invalid date format. Use "2024", "2024, January", "2024, January 15", "January 15, 2024", or "n.d."'
			},
			'Course & Education': {
				required: 'Enter the date of the handout.',
				whitespace: 'Date cannot contain only whitespace.',
				invalidFormat:
					'Invalid date format. Use "2024", "2024, January", "2024, January 15", "January 15, 2024", or "n.d."'
			},
			'Data & Software': {
				required: 'Enter the valid date',
				whitespace: 'Date cannot contain only whitespace.',
				invalidFormat:
					'Invalid date format. Use "2024", "2024, January", "2024, January 15", "January 15, 2024", or "n.d."'
			},
			Multimedia: {
				required: 'Enter the release date.',
				whitespace: 'Release date cannot contain only whitespace.',
				invalidFormat:
					'Invalid date format. Use "2024", "2024, January", "2024, January 15", "January 15, 2024", or "n.d."'
			},
			'Social Media': {
				required: 'Enter the date of the post.',
				whitespace: 'Date cannot contain only whitespace.',
				invalidFormat:
					'Invalid date format. Use "2024", "2024, January", "2024, January 15", "January 15, 2024", or "n.d."'
			},
			'Personal Communication': {
				required: 'Enter the date of the communication.',
				whitespace: 'Date cannot contain only whitespace.',
				invalidFormat:
					'Invalid date format. Use "2024", "2024, January", "2024, January 15", "January 15, 2024", or "n.d."'
			}
		};

		const messages = categoryMessages[category] || {
			required: 'Enter the valid date',
			whitespace: 'Date cannot contain only whitespace',
			invalidFormat:
				'Invalid date format. Use "2024", "2024, January", "2024, January 15", "January 15, 2024", or "n.d."'
		};

		return messages[messageType] || messages.required;
	};

	// Check for null, undefined, or empty string
	if (!dateValue || dateValue === '') {
		return { isValid: false, error: getDateErrorMessage('required') };
	}

	// Check for whitespace-only BEFORE trimming
	if (/^\s+$/.test(dateValue)) {
		return { isValid: false, error: getDateErrorMessage('whitespace') };
	}

	const trimmedDate = dateValue.trim();

	// After trimming, check if empty
	if (trimmedDate === '') {
		return { isValid: false, error: getDateErrorMessage('required') };
	}

	// Allow "n.d." for no date
	if (trimmedDate.toLowerCase() === 'n.d.') {
		return { isValid: true };
	}

	// Pattern 1: Year only (4 digits)
	const yearOnlyPattern = /^\d{4}$/;
	if (yearOnlyPattern.test(trimmedDate)) {
		const year = parseInt(trimmedDate, 10);
		const currentYear = new Date().getFullYear();

		if (year < 1000) {
			return { isValid: false, error: 'Year must be 1000 or later' };
		}
		if (year > currentYear) {
			return {
				isValid: false,
				error: `Year cannot be in the future (current year: ${currentYear})`
			};
		}
		return { isValid: true };
	}

	// Pattern 2: Year, Month (e.g., "2024, January")
	const yearMonthPattern =
		/^\d{4},\s*(January|February|March|April|May|June|July|August|September|October|November|December)$/i;
	if (yearMonthPattern.test(trimmedDate)) {
		const yearMatch = trimmedDate.match(/^\d{4}/);
		if (yearMatch) {
			const year = parseInt(yearMatch[0], 10);
			const currentYear = new Date().getFullYear();

			if (year < 1000) {
				return { isValid: false, error: 'Year must be 1000 or later' };
			}
			if (year > currentYear) {
				return {
					isValid: false,
					error: `Year cannot be in the future (current year: ${currentYear})`
				};
			}
		}
		return { isValid: true };
	}

	// Pattern 3: Year, Month Day (e.g., "2024, January 15")
	const fullDatePattern =
		/^\d{4},\s*(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2}$/i;
	if (fullDatePattern.test(trimmedDate)) {
		// Extract year and validate
		const yearMatch = trimmedDate.match(/^\d{4}/);
		if (yearMatch) {
			const year = parseInt(yearMatch[0], 10);
			const currentYear = new Date().getFullYear();

			if (year < 1000) {
				return { isValid: false, error: 'Year must be 1000 or later' };
			}
			if (year > currentYear) {
				return {
					isValid: false,
					error: `Year cannot be in the future (current year: ${currentYear})`
				};
			}
		}

		// Extract day and validate range
		const dayMatch = trimmedDate.match(/\d{1,2}$/);
		if (dayMatch) {
			const day = parseInt(dayMatch[0], 10);
			if (day < 1 || day > 31) {
				return { isValid: false, error: 'Day must be between 1 and 31' };
			}
		}
		return { isValid: true };
	}

	// Pattern 4: Month Day, Year (e.g., "January 15, 2024")
	const altDatePattern =
		/^(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},\s*\d{4}$/i;
	if (altDatePattern.test(trimmedDate)) {
		const dayMatch = trimmedDate.match(/\d{1,2}/);
		if (dayMatch) {
			const day = parseInt(dayMatch[0], 10);
			if (day < 1 || day > 31) {
				return { isValid: false, error: 'Day must be between 1 and 31' };
			}
		}
		return { isValid: true };
	}

	return {
		isValid: false,
		error: getDateErrorMessage('invalidFormat')
	};
}

/**
 * Validates a single author name
 * Rules:
 * - Cannot be blank/empty
 * - Cannot contain special characters/symbols (only letters, spaces, periods, commas, hyphens, apostrophes allowed)
 * - Cannot contain multiple names (detect commas that aren't followed by space and single letter)
 * - Must have at least initials or a name
 */
export function validateAuthorName(authorName) {
	// Check for null, undefined, or empty string
	if (!authorName || authorName === '') {
		return { isValid: false, error: 'Author name cannot be blank' };
	}

	// Check for whitespace-only BEFORE trimming
	if (/^\s+$/.test(authorName)) {
		return { isValid: false, error: 'Author name cannot contain only whitespace' };
	}

	const trimmedName = authorName.trim();

	// After trimming, check if empty
	if (trimmedName === '') {
		return { isValid: false, error: 'Author name cannot be blank' };
	}

	// Check for numbers - names should not contain numbers
	const numbersPattern = /[0-9]/;
	if (numbersPattern.test(trimmedName)) {
		return {
			isValid: false,
			error:
				'Author name cannot contain numbers. Only letters, spaces, periods, commas, hyphens, and apostrophes are allowed'
		};
	}

	// Check for invalid special characters and symbols
	// Only allow: letters, spaces, periods, commas, hyphens, apostrophes
	// Block ALL other symbols: &, !, @, #, $, %, ^, *, +, =, /, \, |, <, >, etc.
	const invalidCharsPattern = /[!@#$%^&*()_+=[\]{}|\\/:;<>?~`]/;
	if (invalidCharsPattern.test(trimmedName)) {
		return {
			isValid: false,
			error:
				'Author name contains invalid special characters or symbols. Only letters, spaces, periods, commas, hyphens, and apostrophes are allowed'
		};
	}

	// Check for multiple names in one field (detect patterns like "John Doe, Jane Smith")
	// Allow commas only in format like "Smith, J." or "Smith, John"
	const multipleNamesPattern = /,\s+[A-Z][a-z]+\s+[A-Z]/; // Detects ", Name Name"
	if (multipleNamesPattern.test(trimmedName)) {
		return {
			isValid: false,
			error: 'Enter only one author per field. Add additional authors using the "Add" button'
		};
	}

	// Check for suspicious patterns that might indicate multiple names
	const suspiciousPattern = /\band\b/i; // Contains "and"
	if (suspiciousPattern.test(trimmedName)) {
		return {
			isValid: false,
			error: 'Enter only one author per field. Use the "Add" button for additional authors'
		};
	}

	// Check for only whitespace
	if (/^\s+$/.test(trimmedName)) {
		return {
			isValid: false,
			error: 'Author name cannot contain only whitespace'
		};
	}

	// Must contain at least one letter
	if (!/[a-zA-Z]/.test(trimmedName)) {
		return {
			isValid: false,
			error: 'Author name must contain at least one letter'
		};
	}

	return { isValid: true };
}

/**
 * Validates a URL field
 * @param {string} url - The URL value to validate
 * @param {string} category - The citation category (optional) for category-specific error messages
 * @param {string} source - The source type (optional) for source-specific error messages
 */
export function validateURL(url, category = '', source = '') {
	// Check for null, undefined, or empty string
	if (!url || url === '') {
		// Source type specific messages for Web Content
		if (category === 'Web Content') {
			if (source === 'Blog Post') {
				return { isValid: false, error: 'Enter the URL of the blog post.' };
			} else if (source === 'Webpage') {
				return { isValid: false, error: 'Enter the URL of the webpage.' };
			}
			return { isValid: false, error: 'Enter the URL of the blog post.' };
		}
		// Category-specific error message for Social Media
		if (category === 'Social Media') {
			return { isValid: false, error: 'Enter the url of the post.' };
		}
		// Category-specific error messages for Periodicals
		if (category === 'Periodicals') {
			if (source === 'Journal Article') {
				return { isValid: false, error: 'Enter the DOI or URL for the article.' };
			} else if (source === 'Newspaper Article (Print & Online)') {
				return { isValid: false, error: 'Enter the URL of the article.' };
			} else if (source === 'Preprint') {
				return { isValid: false, error: 'Enter the DOI or URL.' };
			}
		}
		// Category-specific error messages for Multimedia
		if (category === 'Multimedia') {
			if (source === 'Webinar/Lecture (recorded & retrievable)') {
				return { isValid: false, error: 'Enter a DOI or URL.' };
			} else if (source === 'YouTube Video') {
				return { isValid: false, error: 'Enter the video URL.' };
			} else if (source === 'Podcast Episode') {
				return { isValid: false, error: 'Enter the URL where episode can be accessed.' };
			} else if (source === 'Infographic/Chart/Graph') {
				return { isValid: false, error: 'Enter the DOI or URL of this work.' };
			}
		}
		// Category-specific error messages for Government & Institutional Publications
		if (category === 'Government & Institutional Publications') {
			if (source === 'Press Release') {
				return { isValid: false, error: 'Enter the URL for press release.' };
			} else if (source === 'Institutional Report') {
				return { isValid: false, error: 'Enter a DOI or URL for the report.' };
			}
		}
		// Category-specific error messages for Data & Software
		if (category === 'Data & Software') {
			if (source === 'Data set') {
				return { isValid: false, error: 'Enter a DOI or URL for the dataset.' };
			}
		}
		// Category-specific error messages for Course & Education
		if (category === 'Course & Education') {
			if (source === 'Course Handout') {
				return { isValid: false, error: 'Enter the URL where the handout is available.' };
			} else if (source === 'Conference Presentation') {
				return { isValid: false, error: 'Enter a DOI or URL for the presentation.' };
			} else if (source === 'Open Educational Resource') {
				return { isValid: false, error: 'Enter the URL for the resource.' };
			}
		}
		return { isValid: false, error: 'URL is required' };
	}

	// Check for whitespace-only BEFORE trimming
	if (/^\s+$/.test(url)) {
		return { isValid: false, error: 'URL cannot contain only whitespace' };
	}

	const trimmedUrl = url.trim();

	// After trimming, check if empty
	if (trimmedUrl === '') {
		// Source type specific messages for Web Content
		if (category === 'Web Content') {
			if (source === 'Blog Post') {
				return { isValid: false, error: 'Enter the URL of the blog post.' };
			} else if (source === 'Webpage') {
				return { isValid: false, error: 'Enter the URL of the webpage.' };
			}
			return { isValid: false, error: 'Enter the URL of the blog post.' };
		}
		// Category-specific error message for Social Media
		if (category === 'Social Media') {
			return { isValid: false, error: 'Enter the url of the post.' };
		}
		// Category-specific error messages for Periodicals
		if (category === 'Periodicals') {
			if (source === 'Journal Article') {
				return { isValid: false, error: 'Enter the DOI or URL for the article.' };
			} else if (source === 'Newspaper Article (Print & Online)') {
				return { isValid: false, error: 'Enter the URL of the article.' };
			} else if (source === 'Preprint') {
				return { isValid: false, error: 'Enter the DOI or URL.' };
			}
		}
		// Category-specific error messages for Multimedia
		if (category === 'Multimedia') {
			if (source === 'Webinar/Lecture (recorded & retrievable)') {
				return { isValid: false, error: 'Enter a DOI or URL.' };
			} else if (source === 'YouTube Video') {
				return { isValid: false, error: 'Enter the video URL.' };
			} else if (source === 'Podcast Episode') {
				return { isValid: false, error: 'Enter the URL where episode can be accessed.' };
			} else if (source === 'Infographic/Chart/Graph') {
				return { isValid: false, error: 'Enter the DOI or URL of this work.' };
			}
		}
		// Category-specific error messages for Government & Institutional Publications
		if (category === 'Government & Institutional Publications') {
			if (source === 'Press Release') {
				return { isValid: false, error: 'Enter the URL for press release.' };
			} else if (source === 'Institutional Report') {
				return { isValid: false, error: 'Enter a DOI or URL for the report.' };
			}
		}
		// Category-specific error messages for Data & Software
		if (category === 'Data & Software') {
			if (source === 'Data set') {
				return { isValid: false, error: 'Enter a DOI or URL for the dataset.' };
			}
		}
		// Category-specific error messages for Course & Education
		if (category === 'Course & Education') {
			if (source === 'Course Handout') {
				return { isValid: false, error: 'Enter the URL where the handout is available.' };
			} else if (source === 'Conference Presentation') {
				return { isValid: false, error: 'Enter a DOI or URL for the presentation.' };
			} else if (source === 'Open Educational Resource') {
				return { isValid: false, error: 'Enter the URL for the resource.' };
			}
		}
		return { isValid: false, error: 'URL is required' };
	}

	// Check if it starts with http:// or https://
	if (!/^https?:\/\//i.test(trimmedUrl)) {
		// Category-specific error message for Web Content
		if (category === 'Web Content') {
			return { isValid: false, error: 'Enter a valid URL' };
		}
		// Category-specific error message for Social Media
		if (category === 'Social Media') {
			return { isValid: false, error: 'Enter a valid URL' };
		}
		// Category-specific error messages for Periodicals
		if (category === 'Periodicals') {
			if (source === 'Journal Article') {
				return { isValid: false, error: 'Enter a valid URL' };
			} else if (source === 'Newspaper Article (Print & Online)') {
				return { isValid: false, error: 'Enter a valid URL' };
			} else if (source === 'Preprint') {
				return { isValid: false, error: 'Enter a valid URL' };
			}
		}
		// Category-specific error messages for Multimedia
		if (category === 'Multimedia') {
			return { isValid: false, error: 'Enter a valid URL' };
		}
		// Category-specific error messages for Government & Institutional Publications
		if (category === 'Government & Institutional Publications') {
			return { isValid: false, error: 'Enter a valid URL' };
		}
		// Category-specific error messages for Data & Software
		if (category === 'Data & Software') {
			return { isValid: false, error: 'Enter a valid URL' };
		}
		// Category-specific error messages for Course & Education
		if (category === 'Course & Education') {
			return { isValid: false, error: 'Enter a valid URL' };
		}
		return { isValid: false, error: 'URL must start with http:// or https://' };
	}

	// Basic URL validation
	try {
		new URL(trimmedUrl);
		return { isValid: true };
	} catch (e) {
		// Category-specific error message for Web Content
		if (category === 'Web Content') {
			return { isValid: false, error: 'Enter a valid URL' };
		}
		// Category-specific error message for Social Media
		if (category === 'Social Media') {
			return { isValid: false, error: 'Enter a valid URL' };
		}
		// Category-specific error messages for Periodicals
		if (category === 'Periodicals') {
			if (source === 'Journal Article') {
				return { isValid: false, error: 'Enter a valid URL' };
			} else if (source === 'Newspaper Article (Print & Online)') {
				return { isValid: false, error: 'Enter a valid URL' };
			} else if (source === 'Preprint') {
				return { isValid: false, error: 'Enter a valid URL' };
			}
		}
		// Category-specific error messages for Multimedia
		if (category === 'Multimedia') {
			return { isValid: false, error: 'Enter a valid URL' };
		}
		// Category-specific error messages for Government & Institutional Publications
		if (category === 'Government & Institutional Publications') {
			return { isValid: false, error: 'Enter a valid URL' };
		}
		// Category-specific error messages for Data & Software
		if (category === 'Data & Software') {
			return { isValid: false, error: 'Enter a valid URL' };
		}
		// Category-specific error messages for Course & Education
		if (category === 'Course & Education') {
			return { isValid: false, error: 'Enter a valid URL' };
		}
		return { isValid: false, error: 'Invalid URL format' };
	}
}

/**
 * Validates a text field (titles, etc.)
 * Rules:
 * - Cannot be only whitespace
 * - Should have meaningful content
 */
export function validateTextField(fieldValue, fieldLabel) {
	// Check for null, undefined, or empty string
	if (!fieldValue || fieldValue === '') {
		return { isValid: false, error: `${fieldLabel} cannot be blank` };
	}

	// Check for whitespace-only BEFORE trimming
	if (/^\s+$/.test(fieldValue)) {
		return { isValid: false, error: `${fieldLabel} cannot contain only whitespace` };
	}

	const trimmedValue = fieldValue.trim();

	// After trimming, check if empty
	if (trimmedValue === '') {
		return { isValid: false, error: `${fieldLabel} cannot be blank` };
	}

	// Must contain at least one alphanumeric character
	if (!/[a-zA-Z0-9]/.test(trimmedValue)) {
		return {
			isValid: false,
			error: `${fieldLabel} must contain at least one letter or number`
		};
	}

	return { isValid: true };
}

/**
 * Validates a year field (used in some forms)
 */
export function validateYear(year) {
	// Check for null, undefined, or empty string
	if (!year || year === '') {
		return { isValid: false, error: 'Year is required' };
	}

	// Check for whitespace-only BEFORE trimming for better error message
	if (/^\s+$/.test(year)) {
		return { isValid: false, error: 'Year cannot contain only whitespace' };
	}

	const trimmedYear = year.trim();

	// After trimming, check if empty
	if (trimmedYear === '') {
		return { isValid: false, error: 'Year is required' };
	}

	// Allow "n.d." for no date
	if (trimmedYear.toLowerCase() === 'n.d.') {
		return { isValid: true };
	}

	// Must be a 4-digit year
	if (!/^\d{4}$/.test(trimmedYear)) {
		return { isValid: false, error: 'Year must be a 4-digit number (e.g., 2024) or "n.d."' };
	}

	const yearNum = parseInt(trimmedYear, 10);
	const currentYear = new Date().getFullYear();

	if (yearNum < 1000) {
		return { isValid: false, error: 'Year must be 1000 or later' };
	}

	if (yearNum > currentYear) {
		return { isValid: false, error: `Year cannot be in the future (current year: ${currentYear})` };
	}

	return { isValid: true };
}

/**
 * Validates a list of authors
 */
export function validateAuthorList(authorList, fieldLabel = 'Author', authorOrOrganization = null) {
	if (!authorList || !Array.isArray(authorList) || authorList.length === 0) {
		// Custom error message for author field
		if (fieldLabel === 'Author(s)' || fieldLabel === 'Author') {
			if (authorOrOrganization === 'Author(s)') {
				return { isValid: false, error: 'Enter the author.' };
			}
			return { isValid: false, error: 'Enter the author or organization.' };
		}
		return { isValid: false, error: `At least one ${fieldLabel.toLowerCase()} is required` };
	}

	// Filter out empty and whitespace-only entries
	const nonEmptyAuthors = authorList.filter((author) => {
		if (!author || typeof author !== 'string') return false;
		return author.trim() !== '';
	});

	if (nonEmptyAuthors.length === 0) {
		// Custom error message for author field
		if (fieldLabel === 'Author(s)' || fieldLabel === 'Author') {
			if (authorOrOrganization === 'Author(s)') {
				return { isValid: false, error: 'Enter the author.' };
			}
			return { isValid: false, error: 'Enter the author or organization.' };
		}
		return { isValid: false, error: `At least one ${fieldLabel.toLowerCase()} is required` };
	}

	// Validate each non-empty author
	for (let i = 0; i < nonEmptyAuthors.length; i++) {
		const validation = validateAuthorName(nonEmptyAuthors[i]);
		if (!validation.isValid) {
			return {
				isValid: false,
				error: validation.error
			};
		}
	}

	return { isValid: true };
}

/**
 * Validates page range fields
 */
export function validatePageRange(startPage, endPage) {
	if (!startPage || startPage.trim() === '') {
		return { isValid: false, error: 'Starting page is required' };
	}

	if (!endPage || endPage.trim() === '') {
		return { isValid: false, error: 'Ending page is required' };
	}

	const start = startPage.trim();
	const end = endPage.trim();

	// Check if they're numbers
	const startNum = parseInt(start, 10);
	const endNum = parseInt(end, 10);

	if (isNaN(startNum)) {
		return { isValid: false, error: 'Starting page must be a number' };
	}

	if (isNaN(endNum)) {
		return { isValid: false, error: 'Ending page must be a number' };
	}

	if (startNum < 1) {
		return { isValid: false, error: 'Starting page must be greater than 0' };
	}

	if (endNum < 1) {
		return { isValid: false, error: 'Ending page must be greater than 0' };
	}

	if (endNum < startNum) {
		return { isValid: false, error: 'Ending page cannot be less than starting page' };
	}

	return { isValid: true };
}

/**
 * Validates numeric fields (volume, issue, season, episode number)
 * Rules:
 * - Must contain only numbers (digits 0-9)
 * - No letters, spaces, or special characters allowed
 */
export function validateNumericField(value, fieldLabel) {
	// Check for null, undefined, or empty string
	if (!value || value === '') {
		return { isValid: false, error: `${fieldLabel} cannot be blank` };
	}

	// Check for whitespace-only BEFORE trimming
	if (/^\s+$/.test(value)) {
		return { isValid: false, error: `${fieldLabel} cannot contain only whitespace` };
	}

	const trimmedValue = value.trim();

	// After trimming, check if empty
	if (trimmedValue === '') {
		return { isValid: false, error: `${fieldLabel} cannot be blank` };
	}

	// Must contain only digits (no letters, spaces, or special characters)
	if (!/^\d+$/.test(trimmedValue)) {
		return {
			isValid: false,
			error: `${fieldLabel} must contain only numbers`
		};
	}

	return { isValid: true };
}

/**
 * Trims all string values in form data
 * Handles nested objects and arrays
 */
export function trimFormData(formData) {
	const trimmedData = {};

	for (const [key, value] of Object.entries(formData)) {
		if (typeof value === 'string') {
			trimmedData[key] = value.trim();
		} else if (Array.isArray(value)) {
			trimmedData[key] = value.map((item) => (typeof item === 'string' ? item.trim() : item));
		} else {
			trimmedData[key] = value;
		}
	}

	return trimmedData;
}

/**
 * Determines the appropriate validation type based on field name and type
 */
function getValidationType(fieldName, fieldType, citationData = {}) {
	const name = fieldName.toLowerCase();

	// Year fields (including originalWorkPublishedYear)
	if (name === 'year' || name.includes('year')) {
		return 'year';
	}

	// Numeric fields
	if (name === 'volume' || name === 'issue' || name === 'season' || name === 'episodenumber') {
		return 'numeric';
	}

	// Version field - numeric for Preprint, text for others (like Dataset/Software)
	if (name === 'version') {
		// Check if this is for a Preprint source
		const source = citationData._selectedSource || citationData.source || citationData.sourceType;
		if (source === 'Preprint') {
			return 'numeric';
		}
		return 'text';
	}

	// Edition and versionEdition can have text like "2nd ed."
	if (name === 'edition' || name === 'versionedition') {
		return 'text';
	}

	// Report numbers
	if (
		name === 'reportnumber' ||
		name === 'publicationnumber' ||
		name === 'publicationnumberaccessionid'
	) {
		return 'text';
	}

	// Use the field type for other cases
	return fieldType;
}

/**
 * Main validation function for citation form
 * Returns { isValid: boolean, errors: object }
 * @param {Array} fields - Array of field configurations
 * @param {Object} citationData - The form data to validate
 * @param {string} selectedSource - The selected source type
 * @param {string} selectedCategory - The selected category (optional) for category-specific error messages
 */
export function validateCitationForm(
	fields,
	citationData,
	selectedSource = '',
	selectedCategory = ''
) {
	const errors = {};
	let isValid = true;

	// First, trim all data
	const trimmedData = trimFormData(citationData);

	// Add selectedSource to trimmedData for validation logic
	trimmedData._selectedSource = selectedSource;
	trimmedData._selectedCategory = selectedCategory;

	fields.forEach((field) => {
		// Skip fields with showIf conditions that aren't met
		if (field.showIf) {
			const dependentValue = trimmedData[field.showIf.field];
			if (dependentValue !== field.showIf.value) {
				return; // Skip this field
			}
		}

		// Skip pageRangeStart and pageRangeEnd for Periodicals Journal Article and Book & Chapter Chapter in Edited Book
		// They are handled separately with a combined error message
		if (
			((selectedCategory === 'Periodicals' && selectedSource === 'Journal Article') ||
				(selectedCategory === 'Book & Chapter' && selectedSource === 'Chapter in Edited Book')) &&
			(field.name === 'pageRangeStart' || field.name === 'pageRangeEnd')
		) {
			return; // Skip individual validation for these fields
		}

		const fieldValue = trimmedData[field.name];

		// Check if field has actual value (not just whitespace)
		let hasValue = false;
		if (Array.isArray(fieldValue)) {
			// For arrays, check if there are non-empty elements
			hasValue = fieldValue.length > 0 && fieldValue.some((item) => item && item.trim() !== '');
		} else if (typeof fieldValue === 'string') {
			// For strings, check if not empty after trim
			hasValue = fieldValue.trim() !== '';
		} else {
			// For other types (booleans, numbers, etc.)
			hasValue = fieldValue !== null && fieldValue !== undefined && fieldValue !== '';
		}

		// Determine validation type based on field name and type
		const validationType = getValidationType(field.name, field.type, trimmedData);

		// Skip validation if field is optional and has no value
		if (!field.required && !hasValue) {
			return;
		}

		// Check if required field is missing
		if (field.required && !hasValue) {
			// Custom error message for date fields (category-specific)
			if (field.type === 'date') {
				// Source-specific messages for Multimedia
				if (selectedCategory === 'Multimedia') {
					if (selectedSource === 'TV Series Episode') {
						errors[field.name] = 'Enter the release date.';
					} else if (selectedSource === 'Webinar/Lecture (recorded & retrievable)') {
						errors[field.name] = 'Enter the recording date.';
					} else if (selectedSource === 'YouTube Video') {
						errors[field.name] = 'Enter the publication date.';
					} else if (selectedSource === 'Podcast Episode') {
						errors[field.name] = 'Enter the episode release date.';
					} else if (selectedSource === 'Infographic/Chart/Graph') {
						errors[field.name] = 'Enter the publication date.';
					} else if (selectedSource === 'Film/Movie') {
						errors[field.name] = 'Enter the release year.';
					} else if (selectedSource === 'Radio Broadcast') {
						errors[field.name] = 'Enter the broadcast date.';
					} else {
						errors[field.name] = 'Enter the release date.';
					}
				}
				// Source-specific messages for Course & Education
				else if (selectedCategory === 'Course & Education') {
					if (selectedSource === 'Course Handout') {
						errors[field.name] = 'Enter the date of handout.';
					} else if (selectedSource === 'Conference Presentation') {
						errors[field.name] = 'Enter a valid date.';
					} else if (selectedSource === 'Conference Session') {
						errors[field.name] = 'Enter the session date.';
					} else {
						errors[field.name] = 'Enter the date of the handout.';
					}
				} else {
					const categoryMessages = {
						'Book & Chapter': 'Enter the valid date',
						Periodicals: 'Enter the publication date.',
						'Web Content': 'Enter the publication date.',
						'Government & Institutional Publications': 'Enter the release date.',
						'Course & Education': 'Enter the date of the handout.',
						'Data & Software': 'Enter the valid date',
						Multimedia: 'Enter the release date.',
						'Social Media': 'Enter the date of the post.',
						'Personal Communication': 'Enter the date of the communication.'
					};
					errors[field.name] = categoryMessages[selectedCategory] || 'Enter the valid date';
				}
			}
			// Custom error message for author/organization fields
			else if (field.name === 'AuthorOrOrganization') {
				errors[field.name] = 'Enter the author or organization.';
			} else if (field.name === 'author') {
				// Check if Author(s) was selected
				const authorOrOrg = trimmedData.AuthorOrOrganization;
				if (authorOrOrg === 'Author(s)') {
					errors[field.name] = 'Enter the author.';
				} else {
					errors[field.name] = 'Enter the author or organization.';
				}
			} else if (field.name === 'organization') {
				// Check if Organization was selected
				const authorOrOrg = trimmedData.AuthorOrOrganization;
				if (authorOrOrg === 'Organization') {
					errors[field.name] = 'Enter the organization.';
				} else {
					errors[field.name] = 'Enter the author or organization.';
				}
			}
			// Category-specific error messages for Web Content
			else if (selectedCategory === 'Web Content') {
				if (field.name === 'titleOfPost') {
					errors[field.name] = 'Enter the title of the blog post.';
				} else if (field.name === 'blogName') {
					errors[field.name] = 'Enter the blog name.';
				} else if (field.name === 'url') {
					// Source type specific messages for URL
					if (selectedSource === 'Blog Post') {
						errors[field.name] = 'Enter the URL of the blog post.';
					} else if (selectedSource === 'Webpage') {
						errors[field.name] = 'Enter the URL of the webpage.';
					} else {
						errors[field.name] = 'Enter the URL of the blog post.';
					}
				} else if (field.name === 'titleOfWebPage') {
					errors[field.name] = 'Enter the title of the web page.';
				} else {
					errors[field.name] = `${field.label} is required`;
				}
			}
			// Category-specific error messages for Social Media
			else if (selectedCategory === 'Social Media') {
				if (field.name === 'publiclyAccessible') {
					errors[field.name] = 'Select whether the post is publicly accessible.';
				} else if (field.name === 'descriptor') {
					errors[field.name] = 'Select a descriptor.';
				} else if (field.name === 'url') {
					errors[field.name] = 'Enter the url of the post.';
				} else {
					errors[field.name] = `${field.label} is required`;
				}
			}
			// Category-specific error messages for Personal Communication
			else if (selectedCategory === 'Personal Communication') {
				if (field.name === 'authorCommunicator') {
					errors[field.name] = 'Enter the name of the communicator.';
				} else {
					errors[field.name] = `${field.label} is required`;
				}
			}
			// Category-specific error messages for Periodicals
			else if (selectedCategory === 'Periodicals') {
				// Journal Article
				if (selectedSource === 'Journal Article') {
					if (field.name === 'year') {
						errors[field.name] = 'Enter the publication year.';
					} else if (field.name === 'titleOfArticle') {
						errors[field.name] = 'Enter the title of the article.';
					} else if (field.name === 'titleOfJournal') {
						errors[field.name] = 'Enter the title of the journal.';
					} else if (field.name === 'volume') {
						errors[field.name] = 'Enter the journal volume number.';
					} else if (field.name === 'url' || field.name === 'doiUrl') {
						errors[field.name] = 'Enter the DOI or URL for the article.';
					} else {
						errors[field.name] = `${field.label} is required`;
					}
				}
				// Newspaper Article
				else if (selectedSource === 'Newspaper Article (Print & Online)') {
					if (field.name === 'titleOfArticle') {
						errors[field.name] = 'Enter the title of the article.';
					} else if (field.name === 'titleOfNewspaper') {
						errors[field.name] = 'Enter the title of the newspaper.';
					} else if (field.name === 'url' || field.name === 'doiUrl') {
						errors[field.name] = 'Enter the URL of the article.';
					} else {
						errors[field.name] = `${field.label} is required`;
					}
				}
				// Magazine Article
				else if (selectedSource === 'Magazine Article (Print & Online)') {
					if (field.name === 'titleOfArticle') {
						errors[field.name] = 'Enter the title of the article.';
					} else if (field.name === 'titleOfMagazine') {
						errors[field.name] = 'Enter the title of the magazine.';
					} else {
						errors[field.name] = `${field.label} is required`;
					}
				}
				// Preprint
				else if (selectedSource === 'Preprint') {
					if (field.name === 'year') {
						errors[field.name] = 'Enter the year of the preprint.';
					} else if (field.name === 'titleOfManuscript') {
						errors[field.name] = 'Enter the title of the manuscript.';
					} else if (field.name === 'repositoryArchiveName') {
						errors[field.name] = 'Enter the repository or archive name.';
					} else if (field.name === 'url' || field.name === 'doiUrl') {
						errors[field.name] = 'Enter the DOI or URL.';
					} else {
						errors[field.name] = `${field.label} is required`;
					}
				} else {
					errors[field.name] = `${field.label} is required`;
				}
			}
			// Category-specific error messages for Multimedia
			else if (selectedCategory === 'Multimedia') {
				// TV Series Episode
				if (selectedSource === 'TV Series Episode') {
					if (field.name === 'writers') {
						errors[field.name] = 'Enter the writer(s).';
					} else if (field.name === 'directors') {
						errors[field.name] = 'Enter the director(s).';
					} else if (field.name === 'executiveProducers') {
						errors[field.name] = 'Enter the executive producer(s).';
					} else if (field.name === 'titleOfEpisode') {
						errors[field.name] = 'Enter the episode title.';
					} else if (field.name === 'titleOfSeries') {
						errors[field.name] = 'Enter the series title.';
					} else if (field.name === 'productionCompany') {
						errors[field.name] = 'Enter the production company or network.';
					} else {
						errors[field.name] = `${field.label} is required`;
					}
				}
				// Webinar/Lecture
				else if (selectedSource === 'Webinar/Lecture (recorded & retrievable)') {
					if (field.name === 'publiclyAccessible') {
						errors[field.name] = 'Select whether the recording is publicly accessible.';
					} else if (field.name === 'presentersLecturers') {
						errors[field.name] = 'Enter the presenter(s) or lecturer(s).';
					} else if (field.name === 'titleOfRecording') {
						errors[field.name] = 'Enter the title of the recording.';
					} else if (field.name === 'descriptor') {
						errors[field.name] = 'Select the recording type.';
					} else if (field.name === 'platform') {
						errors[field.name] = 'Enter the platform where recording is hosted.';
					} else if (field.name === 'url' || field.name === 'doiUrl') {
						errors[field.name] = 'Enter a DOI or URL.';
					} else {
						errors[field.name] = `${field.label} is required`;
					}
				}
				// YouTube Video
				else if (selectedSource === 'YouTube Video') {
					if (field.name === 'publiclyAccessible') {
						errors[field.name] = 'Select whether the video is publicly accessible.';
					} else if (field.name === 'authorUploader') {
						errors[field.name] = 'Enter the author or uploader.';
					} else if (field.name === 'titleOfVideo') {
						errors[field.name] = 'Enter the title of the video.';
					} else if (field.name === 'url') {
						errors[field.name] = 'Enter the video URL.';
					} else {
						errors[field.name] = `${field.label} is required`;
					}
				}
				// Song/Track
				else if (selectedSource === 'Song/Track') {
					if (field.name === 'publiclyAccessible') {
						errors[field.name] = 'Select whether the song track is publicly accessible.';
					} else if (field.name === 'accessMethod') {
						errors[field.name] = 'Select how you accessed this song or track.';
					} else if (field.name === 'artistGroup') {
						errors[field.name] = 'Enter the artist or group.';
					} else if (field.name === 'year') {
						errors[field.name] = 'Enter the year the song was released.';
					} else if (field.name === 'titleOfSong') {
						errors[field.name] = 'Enter the title of the song or track.';
					} else if (field.name === 'descriptor') {
						errors[field.name] = 'Select a descriptor for this source.';
					} else if (field.name === 'titleOfAlbum') {
						errors[field.name] = 'Enter the album title.';
					} else {
						errors[field.name] = `${field.label} is required`;
					}
				}
				// Podcast Episode
				else if (selectedSource === 'Podcast Episode') {
					if (field.name === 'contributors') {
						errors[field.name] = 'Enter at least one contributor or organization.';
					} else if (field.name === 'contributorRoles') {
						errors[field.name] = 'Select a role for the contributor.';
					} else if (field.name === 'titleOfEpisode') {
						errors[field.name] = 'Enter the title of the episode.';
					} else if (field.name === 'descriptor') {
						errors[field.name] = 'Select a descriptor for this episode.';
					} else if (field.name === 'titleOfPodcast') {
						errors[field.name] = 'Enter the title of the podcast.';
					} else if (field.name === 'url') {
						errors[field.name] = 'Enter the URL where episode can be accessed.';
					} else {
						errors[field.name] = `${field.label} is required`;
					}
				}
				// Infographic/Chart/Graph
				else if (selectedSource === 'Infographic/Chart/Graph') {
					if (field.name === 'titleOfWork') {
						errors[field.name] = 'Enter the title of the work.';
					} else if (field.name === 'descriptor') {
						errors[field.name] = 'Select the type of visual.';
					} else if (field.name === 'url' || field.name === 'doiUrl') {
						errors[field.name] = 'Enter the DOI or URL of this work.';
					} else {
						errors[field.name] = `${field.label} is required`;
					}
				}
				// Film/Movie
				else if (selectedSource === 'Film/Movie') {
					if (field.name === 'contributors') {
						errors[field.name] = 'Enter creator(s) or organization.';
					} else if (field.name === 'contributorRoles') {
						errors[field.name] = 'Select a contributor.';
					} else if (field.name === 'titleOfWork') {
						errors[field.name] = 'Enter the title of work.';
					} else {
						errors[field.name] = `${field.label} is required`;
					}
				}
				// Radio Broadcast
				else if (selectedSource === 'Radio Broadcast') {
					if (field.name === 'contributorsOrganization') {
						errors[field.name] =
							'Enter the contributor or organization responsible for the broadcast.';
					} else if (field.name === 'contributorRoles') {
						errors[field.name] = 'Select at least one contributor role.';
					} else if (field.name === 'titleOfEpisode') {
						errors[field.name] = 'Enter the title of episode.';
					} else if (field.name === 'descriptor') {
						errors[field.name] = 'Select the descriptor for this broadcast.';
					} else if (field.name === 'titleOfSeries') {
						errors[field.name] = 'Enter the title of radio series.';
					} else if (field.name === 'productionCompany') {
						errors[field.name] = 'Enter the production company or network.';
					} else {
						errors[field.name] = `${field.label} is required`;
					}
				} else {
					errors[field.name] = `${field.label} is required`;
				}
			}
			// Category-specific error messages for Government & Institutional Publications
			else if (selectedCategory === 'Government & Institutional Publications') {
				// Press Release
				if (selectedSource === 'Press Release') {
					if (field.name === 'titleOfPressRelease') {
						errors[field.name] = 'Enter title of press release.';
					} else if (field.name === 'url') {
						errors[field.name] = 'Enter the URL for press release.';
					} else {
						errors[field.name] = `${field.label} is required`;
					}
				}
				// Institutional Report
				else if (selectedSource === 'Institutional Report') {
					if (field.name === 'year') {
						errors[field.name] = 'Enter the publication year.';
					} else if (field.name === 'titleOfReport') {
						errors[field.name] = 'Enter the report title.';
					} else if (field.name === 'url' || field.name === 'doiUrl') {
						errors[field.name] = 'Enter a DOI or URL for the report.';
					} else {
						errors[field.name] = `${field.label} is required`;
					}
				} else {
					errors[field.name] = `${field.label} is required`;
				}
			}
			// Category-specific error messages for Data & Software
			else if (selectedCategory === 'Data & Software') {
				// Data set
				if (selectedSource === 'Data set') {
					if (field.name === 'year') {
						errors[field.name] = 'Enter the publication year.';
					} else if (field.name === 'titleOfDataset') {
						errors[field.name] = 'Enter dataset title.';
					} else if (field.name === 'url' || field.name === 'doiUrl') {
						errors[field.name] = 'Enter a DOI or URL for the dataset.';
					} else {
						errors[field.name] = `${field.label} is required`;
					}
				} else {
					errors[field.name] = `${field.label} is required`;
				}
			}
			// Category-specific error messages for Course & Education
			else if (selectedCategory === 'Course & Education') {
				// Course Handout
				if (selectedSource === 'Course Handout') {
					if (field.name === 'publiclyAccessible') {
						errors[field.name] = 'Select whether the handout is publicly accessible.';
					} else if (field.name === 'presenterOrganization') {
						errors[field.name] = 'Enter the presenter or organization.';
					} else if (field.name === 'titleOfPresentation') {
						errors[field.name] = 'Enter the title of presentation.';
					} else if (field.name === 'format') {
						errors[field.name] = 'Select a format.';
					} else if (field.name === 'institution') {
						errors[field.name] = 'Enter institution name.';
					} else if (field.name === 'url') {
						errors[field.name] = 'Enter the URL where the handout is available.';
					} else {
						errors[field.name] = `${field.label} is required`;
					}
				}
				// Conference Presentation
				else if (selectedSource === 'Conference Presentation') {
					if (field.name === 'publiclyAccessible') {
						errors[field.name] = 'Select whether the presentation is publicly accessible.';
					} else if (field.name === 'presentersOrganization') {
						errors[field.name] = 'Enter the name of presenter or presenting organization.';
					} else if (field.name === 'titleOfPaper') {
						errors[field.name] = 'Enter the title of paper.';
					} else if (field.name === 'descriptor') {
						errors[field.name] = 'Select a descriptor.';
					} else if (field.name === 'conferenceDetails') {
						errors[field.name] = 'Enter the conference name and details.';
					} else if (field.name === 'url' || field.name === 'doiUrl') {
						errors[field.name] = 'Enter a DOI or URL for the presentation.';
					} else {
						errors[field.name] = `${field.label} is required`;
					}
				}
				// Open Educational Resource
				else if (selectedSource === 'Open Educational Resource') {
					if (field.name === 'year') {
						errors[field.name] = 'Enter a valid year.';
					} else if (field.name === 'titleOfOer') {
						errors[field.name] = 'Enter the title of resource.';
					} else if (field.name === 'platformPublisher') {
						errors[field.name] = 'Enter the platform or publisher hosting the resource.';
					} else if (field.name === 'url') {
						errors[field.name] = 'Enter the URL for the resource.';
					} else {
						errors[field.name] = `${field.label} is required`;
					}
				}
				// Conference Session
				else if (selectedSource === 'Conference Session') {
					if (field.name === 'publiclyAccessible') {
						errors[field.name] = 'Select whether the session is publicly accessible.';
					} else if (field.name === 'contributorsOrganization') {
						errors[field.name] = 'Enter the presenter, panelist or sponsoring organization.';
					} else if (field.name === 'titleOfPresentation') {
						errors[field.name] = 'Enter the title of the presentation.';
					} else if (field.name === 'descriptor') {
						errors[field.name] = 'Select a descriptor.';
					} else if (field.name === 'nameOfConference') {
						errors[field.name] = 'Enter the conference name.';
					} else if (field.name === 'locationOfConference') {
						errors[field.name] = 'Enter the city and state or country of the conference.';
					} else {
						errors[field.name] = `${field.label} is required`;
					}
				} else {
					errors[field.name] = `${field.label} is required`;
				}
			}
			// Category-specific error messages for Book & Chapter
			else if (selectedCategory === 'Book & Chapter') {
				// Book
				if (selectedSource === 'Book') {
					if (field.name === 'authors') {
						errors[field.name] = 'Enter at least one author.';
					} else if (field.name === 'year') {
						errors[field.name] = 'Enter the publication year.';
					} else if (field.name === 'titleOfBook') {
						errors[field.name] = 'Enter the title of book.';
					} else if (field.name === 'publisher') {
						errors[field.name] = 'Enter the publisher name.';
					} else {
						errors[field.name] = `${field.label} is required`;
					}
				}
				// Chapter in Edited Book
				else if (selectedSource === 'Chapter in Edited Book') {
					if (field.name === 'chapterAuthors') {
						errors[field.name] = 'Enter at least one chapter author.';
					} else if (field.name === 'year') {
						errors[field.name] = 'Enter the publication year.';
					} else if (field.name === 'titleOfChapter') {
						errors[field.name] = 'Enter the chapter title.';
					} else if (field.name === 'titleOfBook') {
						errors[field.name] = 'Enter the book title.';
					} else if (field.name === 'publisher') {
						errors[field.name] = 'Enter the publisher name.';
					} else {
						errors[field.name] = `${field.label} is required`;
					}
				}
				// Reference Work Entry
				else if (selectedSource === 'Reference Work Entry') {
					if (field.name === 'entryAuthors') {
						errors[field.name] = 'Enter at least one author or organization.';
					} else if (field.name === 'year') {
						errors[field.name] = 'Enter the publication year.';
					} else if (field.name === 'titleOfEntry') {
						errors[field.name] = 'Enter the entry title.';
					} else if (field.name === 'titleOfReferenceWork') {
						errors[field.name] = 'Enter the reference work title.';
					} else if (field.name === 'publisher') {
						errors[field.name] = 'Enter the publisher name.';
					} else if (field.name === 'url' || field.name === 'doiUrl') {
						errors[field.name] = 'Enter the URL.';
					} else {
						errors[field.name] = `${field.label} is required`;
					}
				}
				// Thesis/Dissertation
				else if (selectedSource === 'Thesis/Dissertation') {
					if (field.name === 'authors') {
						errors[field.name] = 'Enter at least one author.';
					} else if (field.name === 'year') {
						errors[field.name] = 'Enter the publication year.';
					} else if (field.name === 'titleOfThesisDissertation') {
						errors[field.name] = 'Enter the thesis or dissertation title.';
					} else if (field.name === 'descriptor') {
						errors[field.name] = 'Select a descriptor.';
					} else if (field.name === 'institution') {
						errors[field.name] = 'Enter the awarding institution.';
					} else if (field.name === 'publisherRepository') {
						errors[field.name] = 'Enter the database or repository name.';
					} else if (field.name === 'url' || field.name === 'doiUrl') {
						errors[field.name] = 'Enter the URL.';
					} else {
						errors[field.name] = `${field.label} is required`;
					}
				} else {
					errors[field.name] = `${field.label} is required`;
				}
			} else {
				errors[field.name] = `${field.label} is required`;
			}
			isValid = false;
			return;
		}

		// If optional field has only whitespace, reject it
		if (
			!field.required &&
			fieldValue &&
			typeof fieldValue === 'string' &&
			/^\s+$/.test(fieldValue)
		) {
			errors[field.name] = `${field.label} cannot contain only whitespace`;
			isValid = false;
			return;
		}

		// Validate based on type
		let validation = { isValid: true };

		if (validationType === 'list') {
			validation = validateAuthorList(fieldValue, field.label, trimmedData.AuthorOrOrganization);
		} else if (validationType === 'date') {
			validation = validateDate(fieldValue, selectedCategory);
		} else if (validationType === 'url') {
			validation = validateURL(fieldValue, selectedCategory, selectedSource);
		} else if (validationType === 'year') {
			validation = validateYear(fieldValue);
		} else if (validationType === 'numeric') {
			validation = validateNumericField(fieldValue, field.label);
		} else if (validationType === 'text' || validationType === 'textarea') {
			validation = validateTextField(fieldValue, field.label);
		} else if (validationType === 'radio' || validationType === 'select') {
			if (!fieldValue || fieldValue === '') {
				// Custom error message for AuthorOrOrganization radio button
				if (field.name === 'AuthorOrOrganization') {
					validation = { isValid: false, error: 'Enter the author or organization.' };
				} else {
					validation = { isValid: false, error: `${field.label} is required` };
				}
			}
		}

		if (!validation.isValid) {
			errors[field.name] = validation.error;
			isValid = false;
		}
	});

	// Special validation for page ranges (both fields must be filled if one is filled)
	const startField = fields.find((f) => f.name === 'pageRangeStart');
	const endField = fields.find((f) => f.name === 'pageRangeEnd');

	if (startField || endField) {
		const startValue = trimmedData.pageRangeStart;
		const endValue = trimmedData.pageRangeEnd;
		const startFilled = startValue && startValue !== '';
		const endFilled = endValue && endValue !== '';
		const pageRangeRequired = startField?.required || endField?.required;

		// Validate if required OR if either field has a value (both must be filled if one is filled)
		if (pageRangeRequired || startFilled || endFilled) {
			// Check if both fields are missing
			const startMissing = !startValue || startValue.trim() === '';
			const endMissing = !endValue || endValue.trim() === '';

			// Category-specific error message for Periodicals - Journal Article
			if (selectedCategory === 'Periodicals' && selectedSource === 'Journal Article') {
				if (startMissing || endMissing) {
					errors.pageRange = 'Enter the page range for the article.';
					isValid = false;
				}
			}
			// Category-specific error message for Book & Chapter - Chapter in Edited Book
			else if (
				selectedCategory === 'Book & Chapter' &&
				selectedSource === 'Chapter in Edited Book'
			) {
				if (startMissing || endMissing) {
					errors.pageRange = 'Enter the chapter page range.';
					isValid = false;
				}
			} else {
				// For other categories, show individual field errors
				if (startMissing) {
					errors.pageRangeStart = 'Starting page is required';
					isValid = false;
				}
				if (endMissing) {
					errors.pageRangeEnd = 'Ending page is required';
					isValid = false;
				}
			}

			// If both are filled, validate the range
			if (startFilled && endFilled) {
				const validation = validatePageRange(startValue, endValue);
				if (!validation.isValid) {
					// Only set pageRange error if it's not about missing values
					if (!validation.error.includes('required')) {
						errors.pageRange = validation.error;
						isValid = false;
					}
				}
			}
		}
	}

	return { isValid, errors, trimmedData };
}
